import { useMemo } from 'react'
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    Title, Tooltip, Legend, ArcElement, Filler
} from 'chart.js'
import { Line, Bar, Doughnut } from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement, Filler)

// ─── palette (matches GHL tab) ────────────────────────────────────────────────
const C = {
    primary: '#3b82f6',
    sms:     '#06b6d4',
    tfa:     '#f59e0b',
    green:   '#10b981',
    violet:  '#8b5cf6',
    rose:    '#f43f5e',
    slate:   '#64748b',
    dark:    '#0f172a',
}
const PALETTE = [C.tfa, C.sms, C.rose, C.primary, C.green, C.violet, '#f97316', '#14b8a6', '#ec4899', '#a3e635']
const SENTIMENT_COLORS = { Negative: C.rose, Neutral: C.sms, Positive: C.green, Unknown: C.tfa }

// ─── formatting ───────────────────────────────────────────────────────────────
const nf  = n => n == null ? '—' : Number(n).toLocaleString()
const pf  = n => n == null ? '—' : `${Number(n).toFixed(1)}%`
const sf  = n => n == null ? '—' : `${Number(n).toFixed(0)}s`
// const mf  = n => n == null ? '—' : `${Math.round(n)}ms`

// ─── shared components (mirrors MetricsDashboard) ─────────────────────────────
function Scorecard({ label, value, sub, accent = C.primary }) {
    return (
        <div style={{
            background: '#fff', borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 130,
            boxShadow: '0 1px 3px rgba(0,0,0,.06)', borderTop: `3px solid ${accent}`
        }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 10 }}>
                {label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, lineHeight: 1.4 }}>{sub}</div>}
        </div>
    )
}

function Section({ title, accent = C.dark, children }) {
    return (
        <div style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
            <div style={{ background: accent, color: '#fff', padding: '11px 20px', fontWeight: 700, fontSize: 14, letterSpacing: '.2px' }}>
                {title}
            </div>
            <div style={{ background: '#fff', padding: '20px' }}>
                {children}
            </div>
        </div>
    )
}

function CardRow({ children, gap = 12 }) {
    return <div style={{ display: 'flex', gap, flexWrap: 'wrap' }}>{children}</div>
}

function ChartWrap({ title, children, style = {} }) {
    return (
        <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)', ...style }}>
            {title && <p style={{ margin: '0 0 16px', fontWeight: 700, fontSize: 13, color: '#374151', textTransform: 'uppercase', letterSpacing: '.5px' }}>{title}</p>}
            {children}
        </div>
    )
}

function Divider() {
    return <div style={{ height: 1, background: '#f1f5f9', margin: '20px 0' }} />
}

// ─── chart helpers ────────────────────────────────────────────────────────────
const xScale = {
    grid:  { color: '#f1f5f9' },
    ticks: { color: '#9ca3af', font: { size: 10 }, maxRotation: 0, maxTicksLimit: 7 },
}
const yScale = (cb) => ({
    grid:  { color: '#f1f5f9' },
    ticks: { color: '#9ca3af', font: { size: 10 }, ...(cb ? { callback: cb } : {}) },
})
const TTP = { backgroundColor: '#fff', borderColor: '#e2e8f0', borderWidth: 1, titleColor: '#0f172a', bodyColor: '#374151' }
const LEG = { labels: { color: '#374151', font: { size: 11 }, boxWidth: 10, padding: 10 } }

const baseOpts = (yCb) => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: { legend: LEG, tooltip: TTP },
    scales: { x: xScale, y: yScale(yCb) },
})
const stackedOpts = () => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: { legend: LEG, tooltip: TTP },
    scales: { x: { ...xScale, stacked: true }, y: { ...yScale(), stacked: true } },
})
const hBarOpts = () => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: true,
    plugins: { legend: LEG, tooltip: TTP },
    scales: {
        x: { ...xScale, ticks: { ...xScale.ticks, callback: v => v + '%' } },
        y: { ...yScale(), ticks: { color: '#9ca3af', font: { size: 9 } } },
    },
})
const donutOpts = () => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: { legend: { ...LEG, position: 'bottom' }, tooltip: TTP },
})

const areaSeries = (label, values, color = C.primary) => ({
    label,
    data: values,
    borderColor: color,
    backgroundColor: color + '22',
    fill: true,
    tension: 0.4,
    pointRadius: 2,
    pointHoverRadius: 5,
    borderWidth: 2,
})

// ─── main component ───────────────────────────────────────────────────────────
const RetellDashboard = ({ data }) => {
    const labels = useMemo(
        () => data.daily.map(d => {
            const [, m, dy] = d.date.split('-').map(Number)
            return new Date(0, m - 1, dy).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
        }),
        [data.daily]
    )

    const daily = data.daily

    const disconnectKeys = useMemo(
        () => Object.entries(data.disconnection_reasons).sort((a, b) => b[1] - a[1]).map(([k]) => k),
        [data.disconnection_reasons]
    )
    const sentimentKeys = useMemo(() => Object.keys(data.user_sentiment), [data.user_sentiment])

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* ── 1. OVERVIEW ────────────────────────────────────────────── */}
            <Section title="📊  Overview" accent={C.dark}>
                <CardRow>
                    <Scorecard label="Total Calls"     value={nf(data.total_calls)}                               accent={C.primary} />
                    <Scorecard label="Avg Duration"    value={sf(data.avg_duration_s)}                            accent={C.sms} />
                    <Scorecard label="Successful"      value={nf(data.successful_count)}                          accent={C.green} />
                    <Scorecard label="Picked Up"       value={nf(data.picked_up_count)}                           accent={C.green} />
                    <Scorecard label="Voicemail"       value={nf(data.voicemail_count)}                           accent={C.tfa} />
                    <Scorecard label="Transfers"       value={nf(data.transfer_count)}                            accent={C.violet} />
                </CardRow>
                <Divider />
                <CardRow>
                    <Scorecard label="Picked Up Rate"   value={pf(data.picked_up_rate)}       accent={C.green}   sub="Not dial_no_answer / voicemail / busy" />
                    <Scorecard label="Successful Rate"  value={pf(data.call_successful_rate)} accent={C.green}   sub="call_analysis.call_successful" />
                    <Scorecard label="Voicemail Rate"   value={pf(data.voicemail_rate)}       accent={C.tfa}     sub="voicemail_reached ÷ total calls" />
                    <Scorecard label="Transfer Rate"    value={pf(data.transfer_rate)}        accent={C.violet}  sub="call_transfer ÷ total calls" />
                    <Scorecard label="Inbound"          value={nf(data.direction.inbound)}    accent={C.sms} />
                    <Scorecard label="Outbound"         value={nf(data.direction.outbound)}   accent={C.primary} />
                </CardRow>
            </Section>

            {/* ── 2. CALL VOLUME ─────────────────────────────────────────── */}
            <Section title="📈  Call Volume" accent={C.primary}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
                    <ChartWrap title="Call Counts">
                        <Line options={baseOpts()} data={{
                            labels,
                            datasets: [areaSeries('Call counts', daily.map(d => d.count))]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Concurrency Used">
                        <Bar options={baseOpts()} data={{
                            labels,
                            datasets: [{
                                label: 'Concurrency used',
                                data: daily.map(d => d.max_concurrency),
                                backgroundColor: C.primary + '55',
                                borderColor: C.primary,
                                borderWidth: 1,
                                borderRadius: 3,
                            }]
                        }} />
                    </ChartWrap>
                </div>
            </Section>

            {/* ── 3. BREAKDOWNS ──────────────────────────────────────────── */}
            <Section title="🍩  Breakdowns" accent={C.violet}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                    <ChartWrap title="Call Successful">
                        <Doughnut options={donutOpts()} data={{
                            labels: ['Unsuccessful', 'Successful'],
                            datasets: [{ data: [data.call_successful.unsuccessful, data.call_successful.successful], backgroundColor: [C.violet + 'cc', C.sms + 'cc'], borderWidth: 0, hoverOffset: 6 }]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Disconnection Reason">
                        <Doughnut options={donutOpts()} data={{
                            labels: disconnectKeys,
                            datasets: [{ data: disconnectKeys.map(k => data.disconnection_reasons[k]), backgroundColor: PALETTE.map(c => c + 'cc'), borderWidth: 0, hoverOffset: 6 }]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="User Sentiment">
                        <Doughnut options={donutOpts()} data={{
                            labels: sentimentKeys,
                            datasets: [{ data: sentimentKeys.map(k => data.user_sentiment[k]), backgroundColor: sentimentKeys.map(k => (SENTIMENT_COLORS[k] || C.tfa) + 'cc'), borderWidth: 0, hoverOffset: 6 }]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Inbound / Outbound">
                        <Doughnut options={donutOpts()} data={{
                            labels: ['Outbound', 'Inbound'],
                            datasets: [{ data: [data.direction.outbound, data.direction.inbound], backgroundColor: [C.sms + 'cc', C.violet + 'cc'], borderWidth: 0, hoverOffset: 6 }]
                        }} />
                    </ChartWrap>
                </div>
            </Section>

            {/* ── 4. RATE TRENDS ─────────────────────────────────────────── */}
            <Section title="📉  Rate Trends" accent={C.green}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                    <ChartWrap title="Call Picked Up Rate">
                        <Line options={baseOpts(v => v + '%')} data={{
                            labels,
                            datasets: [areaSeries('Picked up rate', daily.map(d => d.picked_up_rate != null ? +d.picked_up_rate.toFixed(1) : null), C.green)]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Call Successful Rate">
                        <Line options={baseOpts(v => v + '%')} data={{
                            labels,
                            datasets: [areaSeries('Successful rate', daily.map(d => d.call_successful_rate != null ? +d.call_successful_rate.toFixed(1) : null), C.primary)]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Voicemail Rate">
                        <Line options={baseOpts(v => v + '%')} data={{
                            labels,
                            datasets: [areaSeries('Voicemail rate', daily.map(d => d.voicemail_rate != null ? +d.voicemail_rate.toFixed(1) : null), C.tfa)]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Call Transfer Rate">
                        <Line options={baseOpts(v => v + '%')} data={{
                            labels,
                            datasets: [areaSeries('Transfer rate', daily.map(d => d.transfer_rate != null ? +d.transfer_rate.toFixed(1) : null), C.violet)]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Average Call Duration">
                        <Line options={baseOpts(v => v + 's')} data={{
                            labels,
                            datasets: [areaSeries('Avg duration', daily.map(d => d.avg_duration_s != null ? +d.avg_duration_s.toFixed(1) : null), C.sms)]
                        }} />
                    </ChartWrap>
                </div>
            </Section>

            {/* ── 5. STACKED BREAKDOWNS ──────────────────────────────────── */}
            <Section title="📊  Daily Breakdowns" accent={C.slate}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                    <ChartWrap title="Call Successful">
                        <Bar options={stackedOpts()} data={{
                            labels,
                            datasets: [
                                { label: 'Successful',   data: daily.map(d => d.successful),           backgroundColor: C.sms    + 'cc', stack: 'a' },
                                { label: 'Unsuccessful', data: daily.map(d => d.count - d.successful), backgroundColor: C.violet + 'cc', stack: 'a' },
                            ]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Disconnection Reason">
                        <Bar options={stackedOpts()} data={{
                            labels,
                            datasets: disconnectKeys.map((reason, i) => ({
                                label: reason,
                                data: daily.map(d => d.disconnection_by_reason[reason] ?? 0),
                                backgroundColor: PALETTE[i % PALETTE.length] + 'cc',
                                stack: 'a',
                            }))
                        }} />
                    </ChartWrap>
                    <ChartWrap title="User Sentiment">
                        <Bar options={stackedOpts()} data={{
                            labels,
                            datasets: sentimentKeys.map((s, i) => ({
                                label: s,
                                data: daily.map(d => d.sentiment_by_type[s] ?? 0),
                                backgroundColor: (SENTIMENT_COLORS[s] || PALETTE[i % PALETTE.length]) + 'cc',
                                stack: 'a',
                            }))
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Call Successful Rate (by day)">
                        <Bar options={hBarOpts()} data={{
                            labels,
                            datasets: [{ label: 'Successful rate', data: daily.map(d => d.call_successful_rate != null ? +d.call_successful_rate.toFixed(1) : null), backgroundColor: C.primary + 'cc' }]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Call Picked Up Rate (by day)">
                        <Bar options={hBarOpts()} data={{
                            labels,
                            datasets: [{ label: 'Picked up rate', data: daily.map(d => d.picked_up_rate != null ? +d.picked_up_rate.toFixed(1) : null), backgroundColor: C.green + 'cc' }]
                        }} />
                    </ChartWrap>
                    <ChartWrap title="Voicemail Rate (by day)">
                        <Bar options={hBarOpts()} data={{
                            labels,
                            datasets: [{ label: 'Voicemail rate', data: daily.map(d => d.voicemail_rate != null ? +d.voicemail_rate.toFixed(1) : null), backgroundColor: C.tfa + 'cc' }]
                        }} />
                    </ChartWrap>
                </div>
            </Section>

        </div>
    )
}

export default RetellDashboard
